/**
 * The unsaved-changes guard (FRM-008, UI-009, FEED-006; TMMRSL-0041): while `when` is true, an
 * in-app navigation to another path is blocked behind a "Discard unsaved changes?" confirmation,
 * and closing or reloading the tab asks the browser's own question. Navigation into `/auth/`
 * (signing out, an expired session) is never blocked. Returns the dialog for the caller to render.
 * Needs a data router (`useBlocker`).
 */
import { createElement, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from './ConfirmDialog';

export function useUnsavedGuard({ when, name }: { when: boolean; name: string }): ReactNode {
  const { t } = useTranslation();
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      when &&
      currentLocation.pathname !== nextLocation.pathname &&
      !nextLocation.pathname.startsWith('/auth/'),
    [when],
  );
  const blocker = useBlocker(shouldBlock);
  // The dialog calls `onCancel` after `onConfirm` too; a proceeding navigation is never reset.
  const proceeding = useRef(false);

  useEffect(() => {
    if (!when) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);

  return createElement(ConfirmDialog, {
    open: blocker.state === 'blocked',
    title: t('unsaved.title'),
    consequence: t('unsaved.body', { name }),
    confirmLabel: t('unsaved.discard'),
    cancelLabel: t('unsaved.keep'),
    destructive: true,
    onConfirm: () => {
      proceeding.current = true;
      blocker.proceed?.();
      return Promise.resolve();
    },
    onCancel: () => {
      if (proceeding.current) {
        proceeding.current = false;
        return;
      }
      blocker.reset?.();
    },
    errorMessage: () => '',
  });
}
